import React, { useEffect, useRef, useState } from 'react';
import { ENDORSEMENT_TYPE, type EndorsementTypeValue, type EndorsementItem, type ExportFormState } from '../../../types/export';
import { type Tag } from '../../../hooks/useTags';
import { TagTypeahead } from '../../../components/TagTypeahead';
import { FormGroup } from '../../../components/FormGroup';
import { SpinnerInput } from '../../../components/SpinnerInput';
import { useProjectIdFromRoute } from '../../../hooks/useProjectIdFromRoute';

interface Props {
  formState: ExportFormState;
  onChange: (patch: Partial<ExportFormState>) => void;
  onValidChange: (valid: boolean) => void;
}

type EndorsementPosition = 'west' | 'south' | 'east';

interface EndorsementFieldProps {
  label: string;
  position: EndorsementPosition;
  value: EndorsementItem[];
  onChange: (items: EndorsementItem[]) => void;
}

function EndorsementField({ label, position, value, onChange }: EndorsementFieldProps) {
  const projectId = useProjectIdFromRoute();
  const idPrefix = `endorsement-${position}`;

  const [mode, setMode] = useState<EndorsementTypeValue>(
    () => value.length > 0 ? value[0].type : ENDORSEMENT_TYPE.None,
  );

  // Sync mode when items arrive from a template load
  const prevValueRef = useRef(value);
  useEffect(() => {
    if (value !== prevValueRef.current && value.length > 0) {
      setMode(value[0].type);
    }
    prevValueRef.current = value;
  }, [value]);

  function handleTypeChange(newType: EndorsementTypeValue) {
    setMode(newType);
    if (newType === ENDORSEMENT_TYPE.Bates) {
      onChange([{ id: 'Bates', name: 'Bates', type: ENDORSEMENT_TYPE.Bates }]);
    } else {
      onChange([]);
    }
  }

  function handleTagChange(tags: Tag[]) {
    onChange(tags.map(t => ({ id: t.id, name: t.name, type: ENDORSEMENT_TYPE.Tag })));
  }

  return (
    <FormGroup label={label} htmlFor="" labelCols="col-sm-4" inputCols="col-sm-8">
      {mode === ENDORSEMENT_TYPE.None && (
        <input type="text" className="form-control" disabled value="" readOnly />
      )}
      {mode === ENDORSEMENT_TYPE.Bates && (
        <input type="text" className="form-control" disabled value="Bates" readOnly />
      )}
      {mode === ENDORSEMENT_TYPE.Text && (
        <input
          type="text"
          className="form-control"
          value={value[0]?.name ?? ''}
          maxLength={255}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v ? [{ id: v, name: v, type: ENDORSEMENT_TYPE.Text }] : []);
          }}
        />
      )}
      {mode === ENDORSEMENT_TYPE.Tag && (
        <TagTypeahead
          projectId={projectId}
          selected={value.map(e => ({ id: e.id, name: e.name, color: '' }))}
          onChange={handleTagChange}
        />
      )}
      <div style={{ paddingTop: 15, fontSize: 12 }}>
        {([
          [ENDORSEMENT_TYPE.None, 'Off'],
          [ENDORSEMENT_TYPE.Bates, 'Bates'],
          [ENDORSEMENT_TYPE.Text, 'Text'],
          [ENDORSEMENT_TYPE.Tag, 'Tag'],
        ] as [EndorsementTypeValue, string][]).map(([val, lbl]) => (
          <div key={val} className="radio radio-primary" style={{ display: 'inline', paddingRight: 5 }}>
            <input
              id={`${idPrefix}-${val}`}
              type="radio"
              checked={mode === val}
              onChange={() => handleTypeChange(val)}
            />
            <label htmlFor={`${idPrefix}-${val}`}>{lbl}</label>
          </div>
        ))}
      </div>
    </FormGroup>
  );
}

export function Step3Naming({ formState, onChange, onValidChange }: Props) {
  const { prefix, startValue, zeroPaddingLength, filesPerDirectory, delimiter } = formState;

  useEffect(() => {
    const valid =
      prefix.trim() !== '' &&
      startValue != null &&
      zeroPaddingLength != null &&
      filesPerDirectory != null;
    onValidChange(valid);
  }, [prefix, startValue, zeroPaddingLength, filesPerDirectory, onValidChange]);

  const preview = `${prefix}${delimiter}${String(startValue ?? 0).padStart(zeroPaddingLength ?? 0, '0')}.ext`;

  return (
    <div>
      {/* Hidden when directToRelativity=true — deferred feature */}
      <div className="row">
        <div className="col-md-6">
          <h5>Endorsements</h5>
          <div className="well well-small" style={{ backgroundColor: '#fff', height: '100%' }}>
            <EndorsementField
              label="Bottom Left"
              position="west"
              value={formState.southWestEndorsement}
              onChange={(items) => onChange({ southWestEndorsement: items })}
            />
            <EndorsementField
              label="Bottom Center"
              position="south"
              value={formState.southEndorsement}
              onChange={(items) => onChange({ southEndorsement: items })}
            />
            <EndorsementField
              label="Bottom Right"
              position="east"
              value={formState.southEastEndorsement}
              onChange={(items) => onChange({ southEastEndorsement: items })}
            />
          </div>
        </div>
        <div className="col-md-6">
          <h5>Native File and Native PDF Naming</h5>
          <div className="well well-small" style={{ backgroundColor: '#fff', height: '100%', overflow: 'auto' }}>
            <div className="radio radio-primary">
              <input
                id="batesNamedNatives"
                type="radio"
                name="nativeFileNameFormat"
                checked={formState.outputOriginalNativePaths === false}
                onChange={() => onChange({ outputOriginalNativePaths: false })}
              />
              <label htmlFor="batesNamedNatives">Bates Numbered</label>
            </div>
            <div className="radio radio-primary">
              <input
                id="originalPathNamedNatives"
                type="radio"
                name="nativeFileNameFormat"
                checked={formState.outputOriginalNativePaths === true}
                onChange={() => onChange({ outputOriginalNativePaths: true })}
              />
              <label htmlFor="originalPathNamedNatives">Original Native Path</label>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-12">
          <h5>Bates Numbering</h5>
          <div className="well well-small" style={{ backgroundColor: '#fff', height: '100%', overflow: 'auto' }}>
            <FormGroup label="Filename Prefix" htmlFor="batesPrefix" inputCols="col-sm-9">
              <input
                id="batesPrefix"
                type="text"
                className="form-control"
                value={prefix}
                maxLength={255}
                required
                onChange={(e) => onChange({ prefix: e.target.value })}
              />
            </FormGroup>
            <FormGroup label="Filename Delimiter" htmlFor="batesDelimiter" inputCols="col-sm-9">
              <input
                id="batesDelimiter"
                type="text"
                className="form-control"
                value={delimiter}
                maxLength={255}
                onChange={(e) => onChange({ delimiter: e.target.value })}
              />
            </FormGroup>
            <FormGroup label="Start Value" htmlFor="batesStartValue" inputCols="col-sm-9">
              <SpinnerInput
                id="batesStartValue"
                value={startValue ?? 0}
                min={0}
                max={999999}
                required
                onChange={(v) => onChange({ startValue: v })}
              />
            </FormGroup>
            <FormGroup label="Zero Padding Length" htmlFor="batesZeroPadding" inputCols="col-sm-9">
              <SpinnerInput
                id="batesZeroPadding"
                value={zeroPaddingLength ?? 0}
                min={0}
                max={20}
                required
                onChange={(v) => onChange({ zeroPaddingLength: v })}
              />
            </FormGroup>
            <FormGroup label={<span className="text-muted">Preview</span>} htmlFor="batesPreview" inputCols="col-sm-9">
              <p id="batesPreview" className="form-control-static">{preview}</p>
            </FormGroup>
            <FormGroup label="Files Per Directory" htmlFor="batesFilesPerDir" inputCols="col-sm-9">
              <SpinnerInput
                id="batesFilesPerDir"
                value={filesPerDirectory ?? 1}
                min={1}
                max={999999}
                required
                onChange={(v) => onChange({ filesPerDirectory: v })}
              />
            </FormGroup>
          </div>
        </div>
      </div>
    </div>
  );
}
